import React, { useEffect, useState } from "react"
import { useQuery } from "@apollo/client"
import styled from "styled-components"
import { ModalFooter } from "reactstrap"
import { Paragraph, Tel } from "@pga/pga-component-library"
import { redirectToLogout } from "../../lib/auth"
import { Button } from "../Button"
import { Modal, ModalBody, Title } from "../Modal"
import { path } from "ramda"
import { pgaComTld } from "../utils"
import { TERMINATE_CODE } from "./terminateCodes"
import "./modal.css"
import { COACH_TOOLS_API } from "../../apolloClient"
import { redirectToMemberApplications } from "../../lib/ptmApplicationsRedirect"
import { useSectionEmploymentVerification } from "../../hooks/useSectionEmploymentVerification"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/associateApplicationEligibility"
import { AFFILIATE_APPLICATION_ELIGIBILITY_QUERY } from "../queries/affiliateApplicationEligibility"
import { usePgmGraduate } from "../../hooks/useMemberApplicationEligibility"
import {
  TERMINATED_MEMBER_APPLICATION_ELIGIBILITY_QUERY,
  TERMINATED_MEMBER_APPLICATIONS_QUERY,
} from "../queries/memberApplicationEligibility"
import { REINSTATEMENT_STEPS_QUERY } from "../queries/reinstatementSteps"
import { GET_PDR_REQUIREMENTS_QUERY } from "../queries/member"
import { flags, useFeatureFlags } from "../../lib/feature-flags"
import BecomeAssociateModal from "../widgets/CourseCurriculum/BecomeAssociateModal"
import {
  ReapplyPathOfferApplyActions,
  ReapplyPathOfferStatuses,
} from "../ReapplyPathOffer"
import { PTM_MEMBERSHIP_APPLICATIONS_QUERY } from "../../utils/prospect/withPtmApplicationsQuery"
import {
  pickLatestApplicationForType,
  PTM_APPLICATION_TYPES,
} from "../../utils/prospect/ptmApplicationStatus"
import {
  areReapplyPathApplicationsReady,
  areReapplyPathEligibilityReady,
  buildReapplyPathOffer,
  isReapplyPathEligible,
  resolvedProgramTypeForReapply,
  shouldFetchReapplyPathEligibility,
} from "../../utils/reapplyPathOffer"
import {
  hasOutstandingReestablishmentFee,
  hasPayableReestablishmentFee,
} from "./reestablishmentFee"
import ReinstatementSteps from "./ReinstatementSteps"

const ModalWrapper = styled(Modal)`
  .modal-content {
    border-radius: 0;
    border: none;
    @media screen and (max-width: 575.98px) {
      text-align: center;
    }
  }
  .modal-footer {
    width: 100%;
    justify-content: center;
    padding-top: 1.5rem;
    padding-bottom: 0;
  }
`

const Body = styled(ModalBody)`
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Telephone = styled(Tel)`
  font-size: 24px;
  margin-bottom: 24px;
`

const MEMBERSHIP_NUMBER = "(800) 474-2776"
const PDR_VIDEO_OPPORTUNITIES_URL =
  "https://resources.pga.org/my-membership/earn-and-report-pdrs/continuing-education-offers-pdr-opportunities"
const PDR_VIDEO_OPPORTUNITIES_COPY =
  "Click Here to view PGA of America PDR Video Opportunities"

const MEMBER_APPLY_CTA_LABELS = {
  reinstate: "Reinstate Membership",
  board_of_control_restoration: "Reinstate Membership",
  reelect: "Re-elect to Membership",
}

// A Board of Control restoration is a second reinstatement, so it follows the
// reinstate flow.
const REINSTATEMENT_TYPES = ["reinstate", "board_of_control_restoration"]

export const memberApplyCtaLabel = reinstatementType =>
  MEMBER_APPLY_CTA_LABELS[reinstatementType] || "Apply for Membership"

export const MEMBER_APPLICATIONS_ERROR_COPY =
  "We could not load your reinstatement application status. Check My Applications for its latest status."
export const MY_APPLICATIONS_LABEL = "My Applications"

const IN_REVIEW_MEMBER_APPLICATION_STATUSES = [
  "submitted",
  "under_review",
  "changes_requested",
]

// Mirrors pga-backend MemberApplicationEligibility: these statuses block a new
// Member application, and approved stays in flight until election.
const isSubmittedReinstatementApplication = application => {
  if (!REINSTATEMENT_TYPES.includes(application?.reinstatementType)) {
    return false
  }
  const status = String(application.status || "").toLowerCase()
  return (
    IN_REVIEW_MEMBER_APPLICATION_STATUSES.includes(status) ||
    (status === "approved" && !application.electedAt)
  )
}

// Re-elect stays closed until eligibility rules are defined.
export const isMemberReelectClosed = reinstatementType =>
  reinstatementType === "reelect"

const withoutApplyCta = action =>
  action ? { ...action, showApplyCta: false } : action

const TerminatedModal = ({ me, duesCycle, orders = [] }) => {
  const [open] = useState(true)
  const [isBecomeAssociateModalOpen, setIsBecomeAssociateModalOpen] =
    useState(false)

  const duesTermination =
    path(["termination", "terminateCd"])(me) === TERMINATE_CODE.NON_PAYMENT
  const reEstablishmentFee = hasOutstandingReestablishmentFee({
    duesCycle,
    orders,
  })
  // TN is a historical terminate reason and can remain after invoices are paid.
  // NPDU only: hide reapply and PDR while a re-establishment fee is outstanding;
  // Pay Dues when the invoice is payable (matches MembershipDues gating).
  const hideReapplyForFee = duesTermination && reEstablishmentFee
  const showPayDuesCta =
    duesTermination && hasPayableReestablishmentFee({ duesCycle, orders })

  // Fetch when program type is not MB. Do not require ST/AP on auth `me` —
  // developers.pga.org type is often missing/wrong for terminated Associates even
  // when Coach Tools eligibility is already true. Do not skip for TN: paid TN
  // records still need re-apply CTAs. Skip only explicit Members (MB); Coach Tools
  // also blocks Members via member_program_or_band?.
  const programType = resolvedProgramTypeForReapply(me)
  const {
    pgmGraduate,
    loading: pgmGraduateLoading,
    error: pgmGraduateError,
  } = usePgmGraduate()
  const shouldFetchEligibility =
    shouldFetchReapplyPathEligibility({
      programType,
    }) &&
    !pgmGraduateLoading &&
    !pgmGraduateError &&
    !pgmGraduate

  const {
    data: associateEligibilityData,
    loading: associateEligibilityLoading,
    error: associateEligibilityError,
  } = useQuery(ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    skip: !shouldFetchEligibility,
  })

  const {
    data: affiliateEligibilityData,
    loading: affiliateEligibilityLoading,
    error: affiliateEligibilityError,
  } = useQuery(AFFILIATE_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    skip: !shouldFetchEligibility,
  })

  // Terminated former Members (MB) reinstate/reelect through the Member
  // application; everyone else keeps the Associate/Affiliate reapply paths.
  // A PGM graduate who was later elected Member is still a former Member, so
  // the graduate check only limits which Member CTA they get (below).
  const [applicationsMembershipFlag] =
    useFeatureFlags([flags.FLAG_FEAT_APPLICATIONS_MEMBERSHIP]) ?? []
  const shouldFetchMemberEligibility =
    applicationsMembershipFlag === true &&
    programType === "MB" &&
    !pgmGraduateLoading &&
    !pgmGraduateError

  const {
    data: memberEligibilityData,
    loading: memberEligibilityLoading,
    error: memberEligibilityError,
  } = useQuery(TERMINATED_MEMBER_APPLICATION_ELIGIBILITY_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    errorPolicy: "all",
    skip: !shouldFetchMemberEligibility,
  })

  const {
    data: ptmApplicationsData,
    loading: ptmApplicationsLoading,
    error: ptmApplicationsError,
  } = useQuery(PTM_MEMBERSHIP_APPLICATIONS_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: !shouldFetchEligibility,
  })

  // Hide PDR videos while an unpaid dues order still has a Re-Establishment
  // Fee: NPDU members can pay that fee to return before the cycle ends.
  const showPdrVideoOpportunities = !hideReapplyForFee

  const membershipApplications = ptmApplicationsError
    ? []
    : ptmApplicationsData?.membershipApplications || []
  const associateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.ASSOCIATE,
  )
  const affiliateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.AFFILIATE,
  )

  const associateEligible = isReapplyPathEligible({
    shouldFetch: shouldFetchEligibility,
    loading: associateEligibilityLoading,
    error: associateEligibilityError,
    eligible:
      associateEligibilityData?.associateApplicationEligibility?.eligible,
  })

  const affiliateEligible = isReapplyPathEligible({
    shouldFetch: shouldFetchEligibility,
    loading: affiliateEligibilityLoading,
    error: affiliateEligibilityError,
    eligible:
      affiliateEligibilityData?.affiliateApplicationEligibility?.eligible,
  })

  const applicationsReady = areReapplyPathApplicationsReady({
    shouldFetch: shouldFetchEligibility,
    loading: ptmApplicationsLoading,
    data: ptmApplicationsData,
    error: ptmApplicationsError,
  })
  const eligibilityReady =
    areReapplyPathEligibilityReady({
      shouldFetch: shouldFetchEligibility,
      loading: associateEligibilityLoading,
      data: associateEligibilityData,
      error: associateEligibilityError,
    }) &&
    areReapplyPathEligibilityReady({
      shouldFetch: shouldFetchEligibility,
      loading: affiliateEligibilityLoading,
      data: affiliateEligibilityData,
      error: affiliateEligibilityError,
    })

  const offer = buildReapplyPathOffer({
    associateEligible,
    affiliateEligible,
    associateApplication,
    affiliateApplication,
    associatePathEligible:
      associateEligibilityData?.associateApplicationEligibility?.pathEligible,
    affiliatePathEligible:
      affiliateEligibilityData?.affiliateApplicationEligibility?.pathEligible,
    applicationsReady,
    eligibilityReady,
  })

  // NPDU with an outstanding re-establishment fee: block reapply until dues are
  // resolved; Pay Dues Now only when the invoice is payable.
  const reapplyActions = hideReapplyForFee
    ? {
        associate: withoutApplyCta(offer.associate),
        affiliate: withoutApplyCta(offer.affiliate),
      }
    : {
        associate: offer.associate,
        affiliate: offer.affiliate,
      }

  // An in-progress Member application makes eligibility false, so read the
  // application itself to keep the steps up with its status. The application's
  // stamped type wins because approval consumes a Board of Control grant.
  const {
    data: memberApplicationsData,
    loading: memberApplicationsLoading,
    error: memberApplicationsError,
  } = useQuery(TERMINATED_MEMBER_APPLICATIONS_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    errorPolicy: "all",
    skip: !shouldFetchMemberEligibility,
  })
  // A partial or missing list could hide the in-flight application, so it fails
  // closed: no new application CTA, only the My Applications recovery path.
  const memberApplicationsList = memberApplicationsData?.membershipApplications
  const memberApplicationsFailed =
    shouldFetchMemberEligibility &&
    !memberApplicationsLoading &&
    (Boolean(memberApplicationsError) || !Array.isArray(memberApplicationsList))
  const memberApplicationsReady =
    !shouldFetchMemberEligibility ||
    (!memberApplicationsLoading && !memberApplicationsFailed)
  const memberApplications =
    !memberApplicationsFailed && Array.isArray(memberApplicationsList)
      ? memberApplicationsList
      : []
  const submittedReinstatementApplication = pickLatestApplicationForType(
    memberApplications.filter(isSubmittedReinstatementApplication),
    "member",
  )
  const { awaitingById, loading: sectionEmploymentLoading } =
    useSectionEmploymentVerification({
      skip: !shouldFetchMemberEligibility,
    })
  const reinstatementApplicationForSteps = submittedReinstatementApplication
    ? {
        ...submittedReinstatementApplication,
        awaitingSectionEmploymentVerification:
          awaitingById.get(submittedReinstatementApplication.id) === true,
      }
    : null

  const reinstatementType =
    memberEligibilityData?.memberApplicationEligibility?.reinstatementType
  // Graduates who never held a Member program apply from Course Curriculum, so
  // they only get a Member CTA here when Coach Tools confirms reinstatement.
  const showApplyForMembershipCta =
    !hideReapplyForFee &&
    memberApplicationsReady &&
    !isMemberReelectClosed(reinstatementType) &&
    (!pgmGraduate || REINSTATEMENT_TYPES.includes(reinstatementType)) &&
    isReapplyPathEligible({
      shouldFetch: shouldFetchMemberEligibility,
      loading: memberEligibilityLoading,
      error: memberEligibilityError,
      eligible: memberEligibilityData?.memberApplicationEligibility?.eligible,
    })

  // Reinstatement is gated on a background check less than a year old in
  // Account. Re-elect is closed until eligibility rules are defined.
  const showReinstatementSteps =
    (!hideReapplyForFee && Boolean(submittedReinstatementApplication)) ||
    (showApplyForMembershipCta &&
      REINSTATEMENT_TYPES.includes(reinstatementType))
  const showMemberApplicationsError =
    !hideReapplyForFee &&
    memberApplicationsFailed &&
    REINSTATEMENT_TYPES.includes(reinstatementType)

  const { data: reinstatementStepsData, loading: reinstatementStepsLoading } =
    useQuery(REINSTATEMENT_STEPS_QUERY, {
      context: { api: COACH_TOOLS_API },
      fetchPolicy: "network-only",
      errorPolicy: "all",
      skip: !showReinstatementSteps,
    })

  // Separate from REINSTATEMENT_STEPS_QUERY so a PDR failure cannot drop the
  // background check data that gates Submit.
  const {
    data: pdrRequirementsData,
    loading: pdrRequirementsLoading,
    error: pdrRequirementsError,
  } = useQuery(GET_PDR_REQUIREMENTS_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    errorPolicy: "all",
    skip: !showReinstatementSteps,
  })
  const pdrRequirements = pdrRequirementsError
    ? null
    : pdrRequirementsData?.currentPerson?.pdrRequirements

  const showAnyReapplyCta = Boolean(
    reapplyActions.associate?.showApplyCta ||
    reapplyActions.affiliate?.showApplyCta ||
    showApplyForMembershipCta,
  )

  const showBecomeAssociateButton = Boolean(
    reapplyActions.associate?.showApplyCta &&
    !reapplyActions.associate?.isResume,
  )

  useEffect(() => {
    if (!showBecomeAssociateButton) {
      setIsBecomeAssociateModalOpen(false)
    }
  }, [showBecomeAssociateButton])

  const showSecondaryLogout =
    showAnyReapplyCta || showPayDuesCta || showMemberApplicationsError

  return (
    <>
      <ModalWrapper
        isOpen={open}
        toggle={() => {}}
        size={showReinstatementSteps ? "lg" : "md"}
        centered
      >
        <Body>
          <Title className="mb-4">Membership Not Current</Title>
          {showPayDuesCta ? (
            <>
              <Paragraph fw="normal" className="mb-2 ml-4">
                Pay your outstanding dues to re-establish your membership.
              </Paragraph>
              <Paragraph fw="normal" className="ml-4">
                For additional support contact PGA Membership Services at:
              </Paragraph>
            </>
          ) : (
            <>
              <Paragraph fw="normal" className="mb-2">
                Your membership is{" "}
                <Paragraph fw="normal" className="d-inline text-danger">
                  <u>not current.</u>
                </Paragraph>
              </Paragraph>
              <Paragraph fw="normal">
                Please contact PGA Membership Services at:
              </Paragraph>
            </>
          )}
          <Telephone phoneNumber={MEMBERSHIP_NUMBER} />
          {showReinstatementSteps && (
            <ReinstatementSteps
              loading={
                reinstatementStepsLoading ||
                (Boolean(submittedReinstatementApplication) &&
                  sectionEmploymentLoading)
              }
              backgroundCheck={
                reinstatementStepsData?.currentMember?.prospect?.backgroundCheck
              }
              backgroundCheckOrder={
                reinstatementStepsData?.currentMember?.prospect
                  ?.backgroundCheckOrder
              }
              applyLabel={memberApplyCtaLabel(reinstatementType)}
              onApply={redirectToMemberApplications}
              application={reinstatementApplicationForSteps}
              pdrRequirements={pdrRequirements}
              pdrRequirementsLoading={pdrRequirementsLoading}
              pdrResourcesUrl={
                showPdrVideoOpportunities ? PDR_VIDEO_OPPORTUNITIES_URL : null
              }
              pdrResourcesCopy={PDR_VIDEO_OPPORTUNITIES_COPY}
            />
          )}
          {showPdrVideoOpportunities && !showReinstatementSteps && (
            <Paragraph fw="normal" className="mb-3">
              <a
                href={PDR_VIDEO_OPPORTUNITIES_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {PDR_VIDEO_OPPORTUNITIES_COPY}
              </a>
            </Paragraph>
          )}
          {showMemberApplicationsError && (
            <Paragraph fw="normal" className="mb-3 text-center">
              {MEMBER_APPLICATIONS_ERROR_COPY}
            </Paragraph>
          )}
          <ReapplyPathOfferStatuses
            associateAction={offer.associate}
            affiliateAction={offer.affiliate}
            associateApplication={associateApplication}
            affiliateApplication={affiliateApplication}
            centered
          />
          <ModalFooter>
            {showPayDuesCta && (
              <Button
                href={`https://${pgaComTld}/member/dues/overview`}
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
              >
                Pay Dues Now
              </Button>
            )}
            {showApplyForMembershipCta && !showReinstatementSteps && (
              <Button
                color="primary"
                className="m-2"
                onClick={redirectToMemberApplications}
              >
                {memberApplyCtaLabel(reinstatementType)}
              </Button>
            )}
            {showMemberApplicationsError && (
              <Button
                color="primary"
                className="m-2"
                onClick={redirectToMemberApplications}
              >
                {MY_APPLICATIONS_LABEL}
              </Button>
            )}
            <ReapplyPathOfferApplyActions
              associateAction={reapplyActions.associate}
              affiliateAction={reapplyActions.affiliate}
              onBecomeAssociate={
                reapplyActions.associate?.isResume
                  ? redirectToMemberApplications
                  : () => setIsBecomeAssociateModalOpen(true)
              }
              onBecomeAffiliate={redirectToMemberApplications}
            />
            <Button
              color={showSecondaryLogout ? "secondary" : "primary"}
              outline={showSecondaryLogout}
              onClick={() => redirectToLogout()}
            >
              Return to Public Content
            </Button>
          </ModalFooter>
        </Body>
      </ModalWrapper>
      <BecomeAssociateModal
        isOpen={isBecomeAssociateModalOpen}
        onClose={() => setIsBecomeAssociateModalOpen(false)}
        onApply={redirectToMemberApplications}
      />
    </>
  )
}

export default TerminatedModal
